/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import ProductCarouselComponent from "@gcg/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {
    erpProductNumbers = "erpProductNumbers",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.erpProductNumbers]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    return {
        products: getProductsForProductInfoList(state, props.id),
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const CuratedProductCarousel: React.FC<Props> = props => {
    const { fields, products, id } = props;
    const dispatch = useDispatch();

    useEffect(() => {
        if (fields.erpProductNumbers?.trim()) {
            const rawProductNumbers = fields.erpProductNumbers.split(",");
            const productNumbers = [];

            for (let i = 0; i < rawProductNumbers.length; i++) {
                const productNumber = rawProductNumbers[i];

                if (productNumber && productNumber.trim()) {
                    productNumbers.push(productNumber.trim());
                }
            }

            dispatch(
                loadProductInfoList({
                    getProductCollectionParameter: { productNumbers } as any,
                    id,
                } as any),
            );
        }
    }, [fields.erpProductNumbers]);

    if (!products || !products.length) {
        return null;
    }

    return (
        <ProductCarouselComponent
            fields={{ ...fields, carouselTemplat: "carousel", showAddToCart: true, showImage:true, showPartNumbers: true, showBrand: true, showPrice: true, showAddToList: true }}
            products={products}
            id={id}
        />
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.erpProductNumbers,
            displayName: "ERP Product Numbers",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "e.g 12345,32156,abc12",
            isRequired: true,
        },
    ] as any,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CuratedProductCarousel),
    definition,
};

export default widgetModule;
